import { formatYYYYMMDDForLocale } from '../utils/dates';
import { IArticleSummary, IGroupedArticles, IThemeWithArticles, IThemeWithGroupedArticles } from '../types/article';

const removePunctuation = (str: string): string => {
  // Regular expression to match all punctuation characters
  const punctuationRegex = /[.,/#!$%^&*;:{}=\-_`~()?"']/g;

  // Replace punctuation with an empty string
  return str.replace(punctuationRegex, '');
};

const msPerDay = 1000 * 60 * 60 * 24;
const daysBetween = (date1: Date, date2: Date): number => {
  return Math.abs(Math.floor((date1.getTime() - date2.getTime()) / msPerDay));
};

/**
 * Articles with the same title, that are published within the same 2 week window, will be grouped together.
 * Grouped articles from different sources are considered similar articles.
 * Grouped articles from the same source will be added to deletedArticles.
 * @param allArticles - List of articles to group.
 * The mainArticle in the response will be the first occurence of an article with that title
 * ie ordering is expected to be done before calling this function.
 * @returns A list of IGroupedArticles objects, representing a grouping of similar articles
 */
export const groupArticles = (allArticles: IArticleSummary[]): IGroupedArticles[] => {
  if (!allArticles.length) {
    return [];
  }

  const titleBuckets: Record<
    string,
    {
      startDate: Date;
      main: IArticleSummary;
      similar: IArticleSummary[];
      deleted: IArticleSummary[];
    }[]
  > = {};

  for (const article of allArticles) {
    const normalisedTitle = removePunctuation(article.title.trim().toLowerCase());
    const publicationDate = new Date(formatYYYYMMDDForLocale(article.publication_datetime));

    if (!titleBuckets[normalisedTitle]) {
      titleBuckets[normalisedTitle] = [
        {
          startDate: publicationDate,
          main: article,
          similar: [],
          deleted: [],
        },
      ];
    } else {
      const buckets = titleBuckets[normalisedTitle];
      let matched = false;

      for (const bucket of buckets) {
        if (daysBetween(publicationDate, bucket.startDate) <= 14) {
          if (article.source_name === bucket.main.source_name) {
            bucket.deleted.push(article);
          } else {
            bucket.similar.push(article);
          }
          matched = true;
          break;
        }
      }

      if (!matched) {
        buckets.push({
          startDate: publicationDate,
          main: article,
          similar: [],
          deleted: [],
        });
      }
    }
  }

  const groupedArticles: IGroupedArticles[] = [];
  for (const bucketList of Object.values(titleBuckets)) {
    for (const bucket of bucketList) {
      groupedArticles.push({
        group_key: bucket.main.dowj_article_id,
        mainArticle: bucket.main,
        similarArticles: bucket.similar,
        deletedArticles: bucket.deleted,
      });
    }
  }

  return groupedArticles;
};

export const createThemesWithGroupedArticles = (themesWithArticles: IThemeWithArticles[]): IThemeWithGroupedArticles[] => {
  if (!themesWithArticles) {
    return [];
  }

  const themesWithGroupedArticles: IThemeWithGroupedArticles[] = [];
  for (const themeWithArticles of themesWithArticles) {
    themesWithGroupedArticles.push({
      id: themeWithArticles.id,
      theme_name: themeWithArticles.theme_name,
      groupedArticles: groupArticles(themeWithArticles.articles),
    });
  }
  return themesWithGroupedArticles;
};
